import { action, computed, makeObservable, observable } from 'mobx';

import { HttpResponse, tournamentController } from '../api';
import { LaunchData, TournamentUserStat } from '../models';
import { GameModeName } from '../constants';
import { isMobile } from '../helpers';
import { env } from '../environment';
import { RootStore } from '.';

export class TournamentStore {

  private rootStore: RootStore;

  public tournamentFrameOpen = false;

  public tournamentFinishedModalOpen = false;

  public tournamentHasClaimModalOpen = false;

  public tournamentInfoModalOpen = false;

  public tournamentUserStat = new HttpResponse<TournamentUserStat>({ score: 0, rank: 0 });

  /**
   * @description TOURNAMENT_ENDS_SOON is scheduled from the sidebar tournament card, which remounts
   * on every promotions-tab visit - this guard keeps the notification scheduled once per session.
   * Not observable: nothing renders from it.
   */
  public endsSoonNotificationScheduled = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {

      showTournamentInfo: computed,

      tournamentFrameOpen: observable,
      setTournamentFrameOpen: action.bound,

      tournamentFinishedModalOpen: observable,
      setTournamentFinishedModalOpen: action.bound,

      tournamentHasClaimModalOpen: observable,
      setTournamentHasClaimModalOpen: action.bound,

      tournamentInfoModalOpen: observable,
      setTournamentInfoModalOpen: action.bound,

      tournamentUserStat: observable,
      setTournamentUserStat: action.bound,

      showTournamentOpener: computed,
    });
  }

  public get showTournamentInfo(): boolean {
    const { data } = this.rootStore.gameStore.launchData;
    if (!data) return false;
    const { tournamentInfo, tournamentId } = data;
    return !!tournamentInfo && tournamentInfo.id !== tournamentId && tournamentInfo.isParticipant;
  }

  public get showTournamentOpener(): boolean {
    const hasCashback = !!this.rootStore.gameStore.launchData.data?.hasCashback;
    const hasPgWheel = !!this.rootStore.gameStore.launchData.data?.wheelInfo;
    const hasTournament = !!this.rootStore.gameStore.launchData.data?.hasTournament;
    const gameReady = this.rootStore.gameStore.gameReady;
    // show tournament opener if user has no cashback and no pg wheel on mobile, or if user has no cashback on desktop
    return gameReady && hasTournament && (isMobile() ? (!hasCashback && !hasPgWheel) : !hasCashback);
  }

  public setTournamentFrameOpen(value: boolean): void {
    this.tournamentFrameOpen = value;
  }

  public setTournamentFinishedModalOpen(value: boolean): void {
    this.tournamentFinishedModalOpen = value;
  }

  public setTournamentHasClaimModalOpen(value: boolean): void {
    this.tournamentHasClaimModalOpen = value;
  }

  public setTournamentInfoModalOpen(value: boolean): void {
    this.tournamentInfoModalOpen = value;
  }

  public setTournamentUserStat(value: HttpResponse<TournamentUserStat>): void {
    this.tournamentUserStat = value;
  }

  public async getTournamentUserStat(tournamentId: number): Promise<void> {
    this.setTournamentUserStat(this.tournamentUserStat.fetching());
    const data = await tournamentController.getTournamentUserStat(tournamentId);
    this.setTournamentUserStat(this.tournamentUserStat.fetched(data));
  }

  public generateLaunchUrl(data: LaunchData): string {

    const {
      gameId, gameTypeId, mode, partnerId, userId, tournamentId, hasClaim, currency,
      currencySymbol, culture, playerExternalId, realModeSwitcher, tournamentTheme, exitUrl
    } = data;

    const url = new URL(`${env.origin}/pg-tournament/`);

    url.searchParams.set('partnerId', `${partnerId}`);
    url.searchParams.set('currency', `${currency}`);
    url.searchParams.set('currencySymbol', `${decodeURIComponent(currencySymbol)}`);
    url.searchParams.set('culture', `${culture}`);
    url.searchParams.set('userId', `${userId}`);
    url.searchParams.set('playerExternalId', `${playerExternalId}`);
    url.searchParams.set('gameId', `${gameId}`);
    url.searchParams.set('gameTypeId', `${gameTypeId}`);
    url.searchParams.set('mode', `${GameModeName[mode]}`);
    url.searchParams.set('theme', `${tournamentTheme}`);
    url.searchParams.set('hasClaim', `${hasClaim}`);
    if (exitUrl) {
      url.searchParams.set('exitUrl', encodeURIComponent(exitUrl));
    }
    if (tournamentId) {
      url.searchParams.set('tournamentId', `${tournamentId}`);
    }
    if (realModeSwitcher) {
      url.searchParams.set('realModeSwitcher', '1');
    }

    return url.toString();
  }

  public openTournamentFrame(tournamentId?: number | null, fromClaim?: boolean): void {
    this.setTournamentFrameOpen(true);
    this.rootStore.postMessageStore.sendTournamentMessage({
      name: 'tournamentFrameOpen',
      tournamentId,
      fromClaim
    });
  }

}
